import { defineStore } from 'pinia'
import { omit, cloneDeep, isEqual } from 'lodash-es'
import { compare } from 'fast-json-patch' // 核心：引入对比函数
import { nanoid } from 'nanoid'
import type { Slide, SlideTheme, PPTElement, PPTAnimation, SlideTemplate } from '@/types/slides'
import * as PPTApi from '@/api/ppt'


interface RemovePropData {
  id: string
  propName: string | string[]
}

interface UpdateElementData {
  id: string | string[]
  props: Partial<PPTElement>
  slideId?: string
}

interface FormatedAnimation {
  animations: PPTAnimation[]
  autoNext: boolean
}

// 定义需要进行 Patch 对比的完整文档结构
interface PPTDocument {
  title: string
  theme: SlideTheme
  slides: Slide[]
}

export interface SlidesState {
  title: string
  theme: SlideTheme
  slides: Slide[]
  slideIndex: number
  viewportSize: number
  viewportRatio: number
  templates: SlideTemplate[]
  outline: any
  ydTemplates: any
  
  // 变更控制相关
  snapshot: PPTDocument | null // 上一次保存的快照
  hasUnsavedChanges: boolean   // 是否有未保存更改
  isSaving: boolean            // 防止重复提交
  baseVersion: number | null   // 增量保存版本
  PPTId: number | null         // ppt的id
}

export const useSlidesStore = defineStore('slides', {
  state: (): SlidesState => ({
    title: '未命名演示文稿', // 幻灯片标题
    theme: {
      themeColors: ['#5b9bd5', '#ed7d31', '#a5a5a5', '#ffc000', '#4472c4', '#70ad47'],
      fontColor: '#333',
      fontName: '',
      backgroundColor: '#fff',
      shadow: {
        h: 3,
        v: 3,
        blur: 2,
        color: '#808080',
      },
      outline: {
        width: 2,
        color: '#525252',
        style: 'solid',
      },
    }, // 主题样式
    slides: [], // 幻灯片页面数据
    slideIndex: 0, // 当前页面索引
    viewportSize: 1280, // 可视区域宽度基数
    viewportRatio: 0.5625, // 可视区域比例，默认16:9
    templates: [
      { name: '山河映红', id: 'template_1', cover: './imgs/template_1.webp', origin: '官方制作' },
      { name: '都市蓝调', id: 'template_2', cover: './imgs/template_2.webp', origin: '官方制作' },
      { name: '智感几何', id: 'template_3', cover: './imgs/template_3.webp', origin: '官方制作' },
      { name: '柔光莫兰迪', id: 'template_4', cover: './imgs/template_4.webp', origin: '官方制作' },
      { name: '简约绿意', id: 'template_5', cover: './imgs/template_5.webp', origin: '社区贡献+官方深度完善优化' },
      { name: '暖色复古', id: 'template_6', cover: './imgs/template_6.webp', origin: '社区贡献+官方深度完善优化' },
      { name: '深邃沉稳', id: 'template_7', cover: './imgs/template_7.webp', origin: '社区贡献+官方深度完善优化' },
      { name: '浅蓝小清新', id: 'template_8', cover: './imgs/template_8.webp', origin: '社区贡献+官方深度完善优化' },
    ], // 模板
    outline:[], // PPT大纲
    ydTemplates:{}, // 生成PPT当前选中的模版
    
    // 初始化状态
    snapshot: null,
    hasUnsavedChanges: false,
    isSaving: false,
    baseVersion:0, //增量保存版本
    PPTId:0, // ppt的id
  }),

  getters: {
    currentSlide(state) {
      return state.slides[state.slideIndex]
    },
    
    // 获取当前的完整文档数据，用于对比
    currentDocument(state): PPTDocument {
      return {
        title: state.title,
        theme: state.theme,
        slides: state.slides
      }
    },

    currentSlideAnimations(state) {
      const currentSlide = state.slides[state.slideIndex]
      if (!currentSlide?.animations) return []
      const els = currentSlide.elements
      const elIds = els.map(el => el.id)
      return currentSlide.animations.filter(animation => elIds.includes(animation.elId))
    },

    formatedAnimations(state) {
        // ... (保持原有的 getter 逻辑不变) ...
        const currentSlide = state.slides[state.slideIndex]
        if (!currentSlide?.animations) return []
        const els = currentSlide.elements
        const elIds = els.map(el => el.id)
        const animations = currentSlide.animations.filter(animation => elIds.includes(animation.elId))
        const formatedAnimations: FormatedAnimation[] = []
        for (const animation of animations) {
            if (animation.trigger === 'click' || !formatedAnimations.length) {
            formatedAnimations.push({ animations: [animation], autoNext: false })
            }
            else if (animation.trigger === 'meantime') {
            const last = formatedAnimations[formatedAnimations.length - 1]
            last.animations = last.animations.filter(item => item.elId !== animation.elId)
            last.animations.push(animation)
            formatedAnimations[formatedAnimations.length - 1] = last
            }
            else if (animation.trigger === 'auto') {
            const last = formatedAnimations[formatedAnimations.length - 1]
            last.autoNext = true
            formatedAnimations[formatedAnimations.length - 1] = last
            formatedAnimations.push({ animations: [animation], autoNext: false })
            }
        }
        return formatedAnimations
    },
  },

  actions: {
    // ==========================================
    // 核心变更管理逻辑 (New)
    // ==========================================

    // 初始化/重置快照 (在页面加载完成或保存成功后调用)
    initSnapshot() {
      this.snapshot = cloneDeep(this.currentDocument)
      this.hasUnsavedChanges = false
    },

    // 检查是否有变更 (供 watcher 调用)
    checkChanges() {
      if (!this.snapshot) return
      // 简单的深比较，如果性能有瓶颈可以优化，但在前端做 diff 触发标记通常很快
      const isChanged = !isEqual(this.snapshot, this.currentDocument)
      this.hasUnsavedChanges = isChanged
    },

    // 保存变更，成功返回 true，失败返回 false
    async saveChanges(): Promise<boolean> {
      if (!this.hasUnsavedChanges || !this.snapshot || this.isSaving) return true

      try {
        this.isSaving = true
        
        // 1. 自动生成 Patch
        // compare(oldObject, newObject) 会自动生成 add/remove/replace/move 操作
        const patches = compare(this.snapshot, this.currentDocument)

        if (patches.length === 0) {
          this.hasUnsavedChanges = false
          return true
        }

        // 2. 构建请求参数
        const saveData = {
          params: {
            pptId: this.PPTId || '',
            baseVersion: this.baseVersion || 1,
            patches // 直接把生成的 patches 发给后端
          }
        }

        // 3. 调用 API
        const res = await PPTApi.PPTDelta(saveData)
        
        if (res.code === 'SUCCESS') {
          this.baseVersion = res.data.newVersion
          // 4. 保存成功后，更新快照为当前最新状态
          this.initSnapshot()
          return true
        }

        // 失败时不更新快照，避免后续增量保存基准错乱
        return false
      } catch (error) {
        return false
      } finally {
        this.isSaving = false
      }
    },

    // ==========================================
    // 常规 Actions (移除了所有手动 Patch 代码)
    // ==========================================
    
    setTitle(title: string) {
      if (!title) this.title = '未命名演示文稿'
      else this.title = title
      // 注意：这里不再需要手动 push patch，也不需要手动设置 hasUnsavedChanges
      // 页面组件的 watcher 会监听到变化并调用 checkChanges
    },

    setTheme(themeProps: Partial<SlideTheme>) {
      this.theme = { ...this.theme, ...themeProps }
    },

    setViewportSize(size: number) {
      this.viewportSize = size
    },

    setViewportRatio(viewportRatio: number) {
      this.viewportRatio = viewportRatio
    },

    setSlides(slides: Slide[], themeProps?: Partial<SlideTheme>) {
      // 直接替换即可，Watcher 会处理 Diff
      const slideIdSet = new Set<string>()
      this.slides = slides.map(slide => {
        const id = String(slide.id || '')
        const nextSlide = { ...slide }
        if (!id || slideIdSet.has(id)) nextSlide.id = nanoid(10)
        slideIdSet.add(String(nextSlide.id))
        return nextSlide
      })
      if (themeProps) this.setTheme(themeProps)
    },

    setTemplates(templates: SlideTemplate[]) {
      this.templates = templates
    },
    
    setYdTemplates(templates: any) {
      this.ydTemplates = templates
    },

    setOutLine(outline: any) {
      this.outline = outline
    },

    updateOutlineItem(id: string | number, updates: Partial<any>) {
        const index = this.outline.findIndex((item: any) => item.id === id);
        if (index !== -1) {
            const newOutline = [...this.outline];
            newOutline[index] = { ...newOutline[index], ...updates };
            this.outline = newOutline;
        }
    },

    addSlide(slide: Slide | Slide[]) {
      const slides = Array.isArray(slide) ? slide : [slide]
      slides.forEach(s => { if (s.sectionTag) delete s.sectionTag })
      const addIndex = this.slideIndex + 1
      this.slides.splice(addIndex, 0, ...slides)
      this.slideIndex = addIndex
    },

    updateSlide(props: Partial<Slide>, slideId?: string) {
      const slideIndex = slideId ? this.slides.findIndex(item => item.id === slideId) : this.slideIndex
      if (slideIndex === -1) return
      
      // 更新状态
      this.slides[slideIndex] = { ...this.slides[slideIndex], ...props }
    },

    removeSlideProps(data: RemovePropData) {
      const { id, propName } = data
      const index = this.slides.findIndex(s => s.id === id)
      if (index !== -1) {
        this.slides[index] = omit(this.slides[index], propName) as Slide
      }
    },

    deleteSlide(slideId: string | string[]) {
      const slidesId = Array.isArray(slideId) ? slideId : [slideId]
      // 过滤掉需要删除的 ID
      this.slides = this.slides.filter(item => !slidesId.includes(item.id))
      
      // 修正当前索引
      if (this.slideIndex >= this.slides.length) {
        this.slideIndex = Math.max(0, this.slides.length - 1)
      }
    },

    deleteSlideByIndex(slideIndex: number | number[]) {
      const slideIndexes = new Set(Array.isArray(slideIndex) ? slideIndex : [slideIndex])
      this.slides = this.slides.filter((_, index) => !slideIndexes.has(index))

      if (this.slideIndex >= this.slides.length) {
        this.slideIndex = Math.max(0, this.slides.length - 1)
      }
    },

    updateSlideIndex(index: number) {
      this.slideIndex = index
    },

    sortSlidesByOutline() {
        if (!this.outline || this.outline.length === 0 || this.slides.length === 0) return
        const slideMap = new Map<string, any>()
        this.slides.forEach(slide => {
            const key = (slide as any).outlinId ?? (slide as any).outlineId
            if (key !== null && key !== undefined && key !== '') {
              slideMap.set(String(key), slide)
            }
        })

        const sortedSlides: any[] = []
        for (const outlineItem of this.outline) {
            const slide = slideMap.get(String(outlineItem.id))
            if (slide) sortedSlides.push(slide)
        }

        // 仅当按大纲匹配到的页数不少于当前时才替换，避免 id 类型不一致丢页
        if (sortedSlides.length >= this.slides.length && sortedSlides.length > 0) {
            this.slides = sortedSlides
        }
        this.slideIndex = 0
    },

    addElement(element: PPTElement | PPTElement[]) {
      const elements = Array.isArray(element) ? element : [element]
      const currentSlide = this.slides[this.slideIndex]
      if (!currentSlide) return

      // 直接修改数组，Pinia 能监听到
      currentSlide.elements.push(...elements)
    },

    deleteElement(elementId: string | string[]) {
      const elementIdList = Array.isArray(elementId) ? elementId : [elementId]
      const currentSlide = this.slides[this.slideIndex]
      if (!currentSlide) return

      currentSlide.elements = currentSlide.elements.filter(item => !elementIdList.includes(item.id))
    },

    updateElement(data: UpdateElementData) {
      const { id, props, slideId } = data
      const elIdList = Array.isArray(id) ? id : [id]
      const slideIndex = slideId ? this.slides.findIndex(item => item.id === slideId) : this.slideIndex
      const slide = this.slides[slideIndex]
      if (!slide) return

      // 更新匹配的元素
      slide.elements = slide.elements.map(el => {
        return elIdList.includes(el.id) ? { ...el, ...props } : el
      })
    },

    removeElementProps(data: RemovePropData) {
      const { id, propName } = data
      const propsNames = Array.isArray(propName) ? propName : [propName]
      const slide = this.slides[this.slideIndex]
      
      slide.elements = slide.elements.map(el => {
        return el.id === id ? omit(el, propsNames) as PPTElement : el
      })
    },

    setBaseVersion(version: number) {
      this.baseVersion = version
    },

    setPPTId(id: number) {
      this.PPTId = id
    },

  },
})
